import { useState, useRef, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AppNotification } from '@hirebridge/types'
import { api } from '../lib/api'

export function NotificationBell() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const bellRef = useRef<HTMLDivElement>(null)

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.notifications(),
    refetchInterval: 60_000,
  })

  const markAll = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const unread = data?.meta.unread ?? 0

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (bellRef.current && !bellRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  return (
    <div className="bell" ref={bellRef}>
      <button
        type="button"
        className={`bell-trigger ${unread > 0 ? 'has-unread' : ''}`}
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        onClick={() => setOpen((o) => !o)}
        title="Notifications"
      >
        <svg className="bell-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {unread > 0 && <span className="bell-count">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="bell-panel" role="region" aria-label="Notifications">
          <header className="bell-panel-header">
            <span className="bell-panel-title">Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                className="bell-mark-read"
                onClick={() => markAll.mutate()}
              >
                Mark all read
              </button>
            )}
          </header>

          <div className="bell-panel-body">
            {(data?.data ?? []).length === 0 ? (
              <div className="bell-empty">
                <span className="bell-empty-icon">🔔</span>
                <p>No new notifications right now.</p>
              </div>
            ) : (
              <ul className="bell-list">
                {(data?.data ?? []).map((n: AppNotification) => (
                  <li key={n.id} className={`bell-item ${n.read ? 'read' : 'unread'}`}>
                    <div className="bell-item-content">
                      <span className="bell-item-msg">{String(n.data.message ?? n.type)}</span>
                      <time className="bell-item-time">{new Date(n.created_at).toLocaleDateString()}</time>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  )
}